import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";

export default function CRMNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [location] = useLocation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: "What is CRM?", href: "/crm/what-is-crm", isLink: true },
    { name: "CRM Software", href: "/crm/crm-software", isLink: true },
    { name: "Features Of CRM", href: "/crm/features", isLink: true },
    { name: "Justdial Integration", href: "/crm/justdial-integration", isLink: true },
    { name: "Sulekha Integration", href: "/crm/sulekha", isLink: true },
  ];

  return (
    <div className="force-light-mode">
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50
          bg-white backdrop-blur-none shadow-md
          transition-all duration-300`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-6 justify-between">
            {/* RESPONSIVE LOGO FOR ALL DEVICES */}
            <Link href="/">
              <img
                src="/logo.png"
                alt="Variable Soft Logo"
                className="h-8 sm:h-9 md:h-10 lg:h-11 xl:h-12 object-contain transition-all duration-300 tablet:h-10 mr-8"
              />
            </Link>

            {/* Desktop Menu - NOW STARTS FROM MD (768px) */}
            <div className="hidden sm:flex items-center space-x-8 lg:space-x-10 xl:space-x-12 tablet:flex">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {item.isLink ? (
                    <Link href={item.href}>
                      <motion.span
                        className={`text-gray-800 hover:text-primary transition-colors cursor-pointer text-sm lg:text-base ${
                          location === item.href ? "text-primary font-semibold" : ""
                        }`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {item.name}
                      </motion.span>
                    </Link>
                  ) : (
                    <motion.button
                      onClick={() => scrollToSection(item.href)}
                      className="text-gray-800 hover:text-primary transition-colors text-sm lg:text-base"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {item.name}
                    </motion.button>
                  )}
                </motion.div>
              ))}

              {/* Optional: Add Contact Button for Tablet+ */}
              <Link href="/getintouch">
                <Button className="rounded-full px-4 py-2 bg-primary hover:bg-primary/90 text-sm lg:text-base ml-2">
                  Contact
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button - HIDDEN ON MD AND ABOVE */}
            <motion.button
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fas fa-bars text-gray-800 text-xl"></i>
            </motion.button>
          </div>

          {/* Mobile Menu - ONLY FOR SMALL DEVICES */}
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden mt-4 pb-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <div key={item.name}>
                    {item.isLink ? (
                      <Link href={item.href}>
                        <span className="text-gray-800 hover:text-primary transition-colors text-left text-base sm:text-lg">
                          {item.name}
                        </span>
                      </Link>
                    ) : (
                      <button
                        onClick={() => scrollToSection(item.href)}
                        className="text-gray-800 hover:text-primary transition-colors text-left text-base sm:text-lg"
                      >
                        {item.name}
                      </button>
                    )}
                  </div>
                ))}
                {/* Mobile Contact Button */}
                <Link href="/getintouch">
                  <Button className="w-full rounded-xl py-3 bg-primary hover:bg-primary/90 mt-4">
                    Contact
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>
    </div>
  );
}
